import {
  Button,
  Modal,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { ICancelModalProps } from "./models";
import { Box, Typography } from "@mui/material";
import DisplayDate from "../../../DisplayDate";
import dayjs from "dayjs";
import { cancelTopMedSchedule } from "../../../../services/topmedSchedule.service";
import { useTranslation } from "react-i18next";

const TRANSLATION_PATH = "page.topmedSchedule.mySchedules.cancelModal.";

export const CancelModal = memo(
  ({ hash, date, patient, open, onClose, onRefresh }: ICancelModalProps) => {
    const {
      t,
      i18n: { language },
    } = useTranslation();

    const { sendRequest, loading } = useFetch(cancelTopMedSchedule);

    const { setMessage } = useNotificationContext();

    const cancelHandler = useCallback(async () => {
      const { success } = await sendRequest(hash);
      if (success) {
        onClose();
        onRefresh();
        setMessage({
          message: t(TRANSLATION_PATH + "success"),
          type: "success",
        });
      }
    }, [hash, t, sendRequest, onClose, onRefresh, setMessage]);

    return (
      <Modal
        open={open}
        onClose={loading ? () => {} : onClose}
        title={`${t(TRANSLATION_PATH + "title")}`}
      >
        <Box
          sx={(t) => ({
            display: "flex",
            flexDirection: "column",
            color: t.palette.grey[t.palette.mode === "light" ? 600 : 400],
          })}
        >
          <Typography>{t(TRANSLATION_PATH + "description")}</Typography>
          <Typography component="span">
            <DisplayDate
              format={
                language === "en-US" ? "MM-DD-YYYY hh:mm A" : "DD/MM/YYYY HH:mm"
              }
              typography={{ component: "span", fontWeight: "bold" }}
            >
              {dayjs(date)}
            </DisplayDate>{" "}
            {`${t(TRANSLATION_PATH + "to")} `}
            <Typography component="span" fontWeight="bold">
              {patient}
            </Typography>
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "end",
              gap: 2,
            }}
          >
            <Button size="small" disabled={loading} onClick={onClose}>
              {t(TRANSLATION_PATH + "cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              loading={loading}
              onClick={cancelHandler}
            >
              {t(TRANSLATION_PATH + "confirm")}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
);
